import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { observedFetch, currentLevel, LEVEL_DEGRADED } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { upsertCachedPlace, upsertCachedResponse, expandSharedSurveys } from "../field_sync_shared"
import { logEvent, logError } from "../field_logger"
import { drainMissionDownloadQueue, connectionLabel } from "../field_mission_download"
import { resumeWarm, healShellCache } from "../field_warm_queue"
import { syncSettings, startDeviceReporting } from "../field_settings_sync"

// Auto-sync controller for bad connectivity places.
//
// Triggers an incremental sync (since + bad_connectivity=true) when:
//   1. Connectivity is restored — listens for the field:online event
//   2. App comes to foreground — visibilitychange → visible, throttled to once per hour
//
// A full manual sync (field_sync_page_controller) must have run at least once
// before auto-sync will fire — we require field:last-prefetch to exist.
//
// On success, dispatches field:prefetch-complete so the offline-app shell re-renders.
//
// Mount on the body alongside field-network-status:
//   data-controller="field-auto-sync"

const ONE_HOUR_MS  = 60 * 60 * 1000
const LAST_AUTO_KEY = "field:last-auto-sync"

// One-off backfill (card #4098). Incremental syncs only send responses that
// changed, so rows cached before a new field was added to the prefetch
// payload never pick it up. When the payload gains a field that existing
// rows need, bump BACKFILL_VERSION: the next page load on a device that has
// synced before re-fetches the full payload once and upserts every row.
// "4098" added missionPriority (place page sort).
// "4117" added missionId and surveyDigest (borrowing a survey from a sibling).
// "4692" added rangingIncoming (open allocation/TIO badge) -- a row already
// cached before an allocation lands never changes its own updated_at, so an
// incremental sync alone would never pick the new field up.
const BACKFILL_KEY     = "field:cache-backfill"
const BACKFILL_VERSION = "4692"

export default class extends Controller {
  connect() {
    this._syncing = false
    this._backfillIfNeeded()
    // Card #4153 -- report the camera and location permission states whenever
    // they change, from any page (not only when Settings is opened).
    startDeviceReporting().catch(() => {})
    this._catchUp("open")

    this._onlineHandler     = () => { this._catchUp("online"); this._triggerIfEligible("auto") }
    this._visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        this._catchUp("reopen")
        this._triggerIfEligible("reopen")
      }
    }

    window.addEventListener("field:online", this._onlineHandler)
    document.addEventListener("visibilitychange", this._visibilityHandler)
  }

  disconnect() {
    window.removeEventListener("field:online", this._onlineHandler)
    document.removeEventListener("visibilitychange", this._visibilityHandler)
  }

  // -- private --

  // Card #4117 -- finishes what a weak signal left undone, whenever the app
  // opens, comes back to the foreground or regains a connection: missions the
  // Tasker asked to download, then pages the last warm pass could not cache.
  // Each is self-limiting (healthy connection only, one run at a time, a
  // minimum gap between runs), so calling them on every trigger is cheap.
  // Card #4659 -- also retries any shell page or build asset a weak-signal
  // install left missing, rather than that staying broken until the next
  // app update's own activate.
  async _catchUp(trigger) {
    try {
      await drainMissionDownloadQueue({ trigger })
      await resumeWarm({ trigger })
      await healShellCache({ trigger })
      // Card #4153 -- queued settings changes go out, and the server's settings
      // come back (a head office change reaches the phone here). Throttled to
      // every 15 minutes unless something is queued.
      await syncSettings()
    } catch (error) {
      logEvent("sync", "Catch-up after weak signal failed, will retry", {
        trigger, message: error?.message || String(error)
      }).catch(() => {})
    }
  }

  // Runs once per device per BACKFILL_VERSION, only after a full sync has
  // happened (nothing cached means nothing to backfill) and only when the
  // connection is healthy. Upserts only: no deletes, no cache warming, so it
  // can't drop anything the manual sync would have kept. If it fails, the
  // marker stays unset and the next page load tries again.
  async _backfillIfNeeded() {
    if (this._readStorage(BACKFILL_KEY) === BACKFILL_VERSION) return
    if (!this._readStorage("field:last-prefetch")) return
    if (!navigator.onLine || currentLevel() === LEVEL_DEGRADED) return
    if (this._syncing) return
    this._syncing = true

    try {
      const res = await observedFetch("/api/v1/field/prefetch.json?dedupe_surveys=1", {
        method:  "GET",
        headers: { "Accept": "application/json" },
        signal:  AbortSignal.timeout(timeoutFor("prefetch", currentLevel()))
      }, { kind: "prefetch", sizeClass: "small" })
      if (!res.ok) return

      const { responses, synced_at } = expandSharedSurveys(await res.json())
      const seenPlaceIds = new Set()
      for (const r of (responses || [])) {
        if (r.place?.id && !seenPlaceIds.has(r.place.id)) {
          seenPlaceIds.add(r.place.id)
          await upsertCachedPlace(db, r.place)
        }
        await upsertCachedResponse(db, r, synced_at)
      }

      this._writeStorage(BACKFILL_KEY, BACKFILL_VERSION)
      logEvent("sync", "Cache backfill complete", { version: BACKFILL_VERSION, count: (responses || []).length }).catch(() => {})
      window.dispatchEvent(new CustomEvent("field:prefetch-complete", { bubbles: true }))
    } catch (error) {
      logEvent("sync", "Cache backfill failed, will retry next load", {
        version: BACKFILL_VERSION, message: error?.message || String(error)
      }).catch(() => {})
    } finally {
      this._syncing = false
    }
  }

  _readStorage(key) {
    try { return localStorage.getItem(key) } catch { return null }
  }

  _writeStorage(key, value) {
    try { localStorage.setItem(key, value) } catch { /* private window: retried next load */ }
  }

  _triggerIfEligible(trigger) {
    const since = localStorage.getItem("field:last-prefetch")
    if (!since) return // no full sync yet — skip

    // Don't compete for a connection that's already struggling (ADR-0110
    // §4). Resumes naturally on the next eligible trigger (field:online or
    // visibilitychange) once level returns to Online -- no explicit "resume"
    // logic needed here.
    if (currentLevel() === LEVEL_DEGRADED) return

    const lastAuto = localStorage.getItem(LAST_AUTO_KEY)
    if (lastAuto && Date.now() - new Date(lastAuto).getTime() < ONE_HOUR_MS) return

    this._autoSync(since, trigger)
  }

  async _autoSync(since, trigger) {
    if (this._syncing) return
    this._syncing = true

    const startedAt = Date.now()
    logEvent("sync", "Auto-sync started", { trigger, level: connectionLabel() }).catch(() => {})

    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      const url = `/api/v1/field/prefetch.json?since=${encodeURIComponent(since)}&bad_connectivity=true&dedupe_surveys=1`

      const res = await observedFetch(url, {
        method:  "GET",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken },
        signal:  AbortSignal.timeout(timeoutFor("prefetch", currentLevel()))
      }, { kind: "prefetch", sizeClass: "small" })

      if (!res.ok) {
        logError("sync", "Auto-sync failed", {
          trigger, status: res.status, durationMs: Date.now() - startedAt, level: connectionLabel()
        }).catch(() => {})
        return
      }

      const text = await res.text()
      const bytes = new Blob([text]).size
      const { responses, removed_ids, synced_at } = expandSharedSurveys(JSON.parse(text))

      // Handle tombstones
      for (const id of (removed_ids || [])) {
        const local = await db.cached_responses.where("responseId").equals(id).first()
        if (!local) continue
        const hasData = local.resultJson && Object.keys(local.resultJson).length > 0
        if (hasData) {
          await db.cached_responses.update(local.id, { status: "pending_upload" })
        } else {
          await db.cached_responses.delete(local.id)
        }
      }

      // Upsert places into cached_places (deduplicated by placeId)
      const seenPlaceIds = new Set()
      for (const r of (responses || [])) {
        if (!r.place?.id || seenPlaceIds.has(r.place.id)) continue
        seenPlaceIds.add(r.place.id)
        await upsertCachedPlace(db, r.place)
      }

      // Upsert responses (no inline place object)
      // Card #4117 -- count what actually landed, so the log shows downloaded
      // against assigned instead of just "started" and silence.
      let saved = 0
      for (const r of (responses || [])) {
        try {
          if (!r.survey_json) continue
          await upsertCachedResponse(db, r, synced_at)
          saved++
        } catch { /* counted below as not saved */ }
      }

      localStorage.setItem("field:last-prefetch", synced_at)
      localStorage.setItem(LAST_AUTO_KEY, new Date().toISOString())

      logEvent("sync", "Auto-sync complete", {
        trigger, assigned: (responses || []).length, downloaded: saved,
        failed: (responses || []).length - saved, removed: (removed_ids || []).length,
        bytes, durationMs: Date.now() - startedAt, level: connectionLabel()
      }).catch(() => {})

      window.dispatchEvent(new CustomEvent("field:prefetch-complete", { bubbles: true }))
      window.dispatchEvent(new CustomEvent("field:mission-counts-changed"))
    } catch (error) {
      // Auto-sync is best-effort -- a killed/timed-out fetch will simply
      // retry on the next eligible trigger, so this is an `event`, not an
      // `error` (semantics table, docs/dev_patterns/pwa-diagnostics-telemetry.md).
      logEvent("sync", "Auto-sync failed, will retry next cycle", {
        trigger, message: error?.message || String(error),
        durationMs: Date.now() - startedAt, level: connectionLabel()
      }).catch(() => {})
    } finally {
      this._syncing = false
    }
  }
}
